/** A SNOMED-CT option for pairing with `SNOMED_SYSTEM` in a `Coding`. */
export type SnomedOption = { code: string; display: string };

/**
 * Curated SNOMED-CT occupation options for an external care-team member's practitioner
 * type (`PractitionerRole.code`). Codes verified against the SNOMED International Edition
 * (tx.fhir.org, 2025-02-01); displays are US-English synonyms of the verified concepts.
 */
export const EXTERNAL_PRACTITIONER_TYPE_OPTIONS: readonly SnomedOption[] = [
  { code: '309343006', display: 'Physician' },
  { code: '62247001', display: 'Family medicine specialist' },
  { code: '17561000', display: 'Cardiologist' },
  { code: '224571005', display: 'Nurse practitioner' },
  { code: '449161006', display: 'Physician assistant' },
  { code: '224535009', display: 'Registered nurse' },
  { code: '46255001', display: 'Pharmacist' },
  { code: '159033005', display: 'Dietitian' },
  { code: '36682004', display: 'Physical therapist' },
  { code: '80546007', display: 'Occupational therapist' },
  { code: '59944000', display: 'Psychologist' },
  { code: '106328005', display: 'Social worker' },
  { code: '3842006', display: 'Chiropractor' },
  { code: '106289002', display: 'Dentist' },
];

/**
 * Curated SNOMED-CT specialty options (`PractitionerRole.specialty`). Drawn from the FHIR
 * R4 `c80-practice-codes` value set (verified against the copy shipped in
 * `@medplum/definitions`), plus Pediatrics (394537008, verified active in SNOMED but not
 * enumerated in c80).
 */
export const EXTERNAL_SPECIALTY_OPTIONS: readonly SnomedOption[] = [
  { code: '419772000', display: 'Family practice' },
  { code: '394814009', display: 'General practice' },
  { code: '419192003', display: 'Internal medicine' },
  { code: '394537008', display: 'Pediatrics' },
  { code: '394579002', display: 'Cardiology' },
  { code: '408480009', display: 'Clinical immunology' },
  { code: '394582007', display: 'Dermatology' },
  { code: '394583002', display: 'Endocrinology' },
  { code: '394584008', display: 'Gastroenterology' },
  { code: '394811001', display: 'Geriatric medicine' },
  { code: '394807007', display: 'Infectious diseases' },
  { code: '394593009', display: 'Medical oncology' },
  { code: '394589003', display: 'Nephrology' },
  { code: '394591006', display: 'Neurology' },
  { code: '394585009', display: 'Obstetrics and gynecology' },
  { code: '394594003', display: 'Ophthalmology' },
  { code: '418960008', display: 'Otolaryngology' },
  { code: '394806003', display: 'Palliative medicine' },
  { code: '394587001', display: 'Psychiatry' },
  { code: '418112009', display: 'Pulmonary medicine' },
  { code: '394810000', display: 'Rheumatology' },
  { code: '394801008', display: 'Trauma and orthopedics' },
  { code: '394612005', display: 'Urology' },
];
